import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao, Cartao } from '../../componentes/base';
import { Alternador } from '../../componentes/formulario';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro } from '../../nucleo/api';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';

const CAMINHO = '/configuracoes/exportacoes-sem-acesso';

/**
 * Ficha de 29/10 (item 1): Admin > Configurações > Empresa > "Arquivos já exportados de relatório sem acesso", no padrão do
 * cartão "Troca de empresa/vertical". Ligado (padrão) = `bloquear`: quem perde a página do relatório ou a permissão do
 * módulo deixa de ver e baixar os arquivos já gerados; desligado = `manter` até expirar. Leitura e gravação pedem
 * `configuracoes.editar`: sem ela, o cartão não aparece.
 */
export function CartaoExportacoesSemAcesso() {
  const s = useSessao();
  if (!podeNoWorkspace(s, 'configuracoes.editar')) return null;
  return <Conteudo />;
}

function Conteudo() {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: [CAMINHO], queryFn: () => get<{ modo: 'bloquear' | 'manter' }>(CAMINHO) });
  const salvo = q.data ? q.data.modo === 'bloquear' : true;
  const [bloquear, setBloquear] = useState(salvo);
  useEffect(() => setBloquear(salvo), [salvo]);
  const sujo = !!q.data && bloquear !== salvo;
  const salvar = useMutation({
    mutationFn: () => api('PUT', CAMINHO, { modo: bloquear ? 'bloquear' : 'manter' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [CAMINHO] });
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Cartao titulo={t('empresa.exportacoes.titulo')}>
      <div className="flex flex-col gap-4" data-exportacoes-sem-acesso="">
        <p className="text-sm text-tinta-3">{t('empresa.exportacoes.ajuda')}</p>
        <div className="flex items-center justify-between gap-3 rounded-lg border border-borda px-3 py-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-tinta-1">{t('empresa.exportacoes.bloquear')}</p>
            <p className="text-xs text-tinta-3">{t('empresa.exportacoes.bloquearAjuda')}</p>
          </div>
          <Alternador marcado={bloquear} aoMudar={setBloquear} rotulo={t('empresa.exportacoes.bloquear')} desabilitado={!q.data} />
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Botao variante="fantasma" disabled={!sujo} onClick={() => setBloquear(salvo)}>
            {t('empresa.exportacoes.descartar')}
          </Botao>
          <Botao disabled={!sujo} carregando={salvar.isPending} onClick={() => salvar.mutate()} data-salvar-exportacoes="">
            {t('empresa.exportacoes.salvar')}
          </Botao>
        </div>
      </div>
    </Cartao>
  );
}
